import {
	Attachment,
	Button,
	COMMON_ATTACHMENT_FORMATS_MIME_MAP,
	DatePicker,
	FileDropperWithAttachments,
	ISO_DATE_FORMAT,
	LinkButton,
	MuiAlert,
	NumberInput,
	ResponsiveDrawer,
	Select,
	Spin,
	strIsNullOrEmpty
} from '@fox/atoms';
import { sendUpdate } from '@fox/extensions';
import { FlexColumnSpaced, FlexRowSpaced, Padded } from '@fox/layout';
import { Form, Formik, FormikHelpers as FormikActions } from 'formik';
import moment from 'moment';
import { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useGoalsContext } from '../../../goals-context';
import { REFETCH_GOALS } from '../../../topics';
import { useGoalsAttachmentSas, useUpsertGoalActivity } from '../../mutation-hooks/mutation-implementations';
import {
	GoalActivityStatus,
	GoalsActivitySource,
	SwiftFoxGoalActivity,
	SwiftFoxGoalPointsCategory,
	UpsertGoalActivityModel
} from '../contracts';
import { addActivityValidationSchema } from './activity-validation-schema';
export function ActivityDrawerDesktop({
	useLinkedOrganisationData,
	defaultStatus,
	activityLabel,
	record
}: {
	useLinkedOrganisationData: boolean;
	activityLabel: string;
	defaultStatus?: string;
	record?: SwiftFoxGoalActivity;
}) {
	const [open, setOpen] = useState<boolean>(false);
	const [needsRefetch, setNeedsRefetch] = useState<boolean>(false);
	return (
		<>
			{record?.id ? (
				<LinkButton onClick={() => setOpen(true)}>{`Edit ${activityLabel.toLowerCase()}`}</LinkButton>
			) : (
				<Button onClick={() => setOpen(true)}>{`Add ${activityLabel.toLowerCase()}`}</Button>
			)}
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title={`${record?.id ? 'Update' : 'Add'} ${activityLabel.toLowerCase()}`}
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
					if (needsRefetch) {
						sendUpdate(REFETCH_GOALS);
					}
				}}
			>
				<UpsertActivity
					useLinkedOrganisationData={useLinkedOrganisationData}
					defaultStatus={defaultStatus}
					record={record}
					closeDrawer={() => setOpen(false)}
					setNeedsRefetch={setNeedsRefetch}
					activityLabel={activityLabel}
				/>
			</ResponsiveDrawer>
		</>
	);
}

export function ActivityDrawerMobile({
	useLinkedOrganisationData,
	defaultStatus,
	record,
	activityLabel
}: {
	useLinkedOrganisationData: boolean;
	activityLabel: string;
	record?: SwiftFoxGoalActivity;
	defaultStatus?: string;
}) {
	const [open, setOpen] = useState<boolean>(false);
	const [needsRefetch, setNeedsRefetch] = useState<boolean>(false);
	return (
		<>
			{record?.id ? (
				<LinkButton onClick={() => setOpen(true)}>{`Edit ${activityLabel.toLowerCase()}`}</LinkButton>
			) : (
				<Button onClick={() => setOpen(true)}>{`Add ${activityLabel.toLowerCase()}`}</Button>
			)}
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title={`${record?.id ? 'Update' : 'Add'} ${activityLabel.toLowerCase()}`}
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
					if (needsRefetch) {
						sendUpdate(REFETCH_GOALS);
					}
				}}
			>
				<UpsertActivity
					useLinkedOrganisationData={useLinkedOrganisationData}
					defaultStatus={defaultStatus}
					record={record}
					closeDrawer={() => setOpen(false)}
					setNeedsRefetch={setNeedsRefetch}
					activityLabel={activityLabel}
				/>
			</ResponsiveDrawer>
		</>
	);
}
function getInitialValues(
	record: SwiftFoxGoalActivity,
	useLinkedOrganisationData: boolean,
	contentId: string,
	activitySources: GoalsActivitySource[],
	defaultStatus?: string
): UpsertGoalActivityModel {
	const baseValues = {
		contentId,
		useLinkedOrganisationData
	};
	if (record?.id) {
		return {
			...baseValues,
			dateRecorded: record.dateRecorded,
			documents: record.documents,
			effectiveDate: record.effectiveDate,
			id: record.id,
			pointCategory: record.points?.[0]?.pointCategoryId,
			points: record.points?.[0]?.points,
			sourceId: record.sourceId,
			status: record.status,
			source: activitySources?.find(s => s.id === record.sourceId)
		};
	} else {
		return {
			...baseValues,
			dateRecorded: moment().format(ISO_DATE_FORMAT),
			status: !strIsNullOrEmpty(defaultStatus) ? defaultStatus : GoalActivityStatus.Pending
		};
	}
}
function UpsertActivity({
	useLinkedOrganisationData,
	defaultStatus,
	activityLabel,
	record,
	closeDrawer,
	setNeedsRefetch
}: {
	useLinkedOrganisationData: boolean;
	closeDrawer: () => void;
	setNeedsRefetch: React.Dispatch<React.SetStateAction<boolean>>;
	activityLabel: string;
	defaultStatus?: string;
	record?: SwiftFoxGoalActivity;
}) {
	const { contentId, requestToken } = useContentContext();
	const { getGoalPointsCategories, getGoalsConfig } = useGoalsContext();
	const { error, loading, mutate, data } = useUpsertGoalActivity({ requestToken });
	const { error: sasError, loading: sasLoading, mutate: getSasMutation } = useGoalsAttachmentSas({ requestToken });
	const submit = async (values: UpsertGoalActivityModel, actions: FormikActions<UpsertGoalActivityModel>) => {
		await mutate({
			contentId: values.contentId,
			dateRecorded: values.dateRecorded,
			effectiveDate: values.effectiveDate,
			status: values.status,
			documents: values.documents,
			source: values.source,
			sourceId: values.sourceId,
			useLinkedOrganisationData,
			points: [{ pointCategoryId: values.pointCategory, points: values.points }],
			...(!strIsNullOrEmpty(values.id) ? { id: values.id } : {})
		});
		setNeedsRefetch(true);
	};
	const pointCategories = getGoalPointsCategories(useLinkedOrganisationData);
	const config = getGoalsConfig();
	const activitySources = config.activitySources
		?.filter(s => {
			return s.ownerType === (useLinkedOrganisationData ? 'Organization' : 'Person');
		})
		.sort((a, b) => a.name.localeCompare(b.name));

	if (data === true) {
		return (
			<MuiAlert severity="success">
				{record?.id ? `${activityLabel} updated successfully` : `${activityLabel} added successfully`}
			</MuiAlert>
		);
	}

	return (
		<FlexColumnSpaced>
			<Formik<UpsertGoalActivityModel>
				initialValues={getInitialValues(record, useLinkedOrganisationData, contentId, activitySources, defaultStatus)}
				validationSchema={addActivityValidationSchema}
				onSubmit={submit}
			>
				{({ isSubmitting, touched, errors, setFieldValue, values, submitForm, dirty }) => {
					return (
						<Form>
							<Spin spinning={isSubmitting || loading}>
								<Padded>
									<FlexColumnSpaced>
										<DatePicker
											size="medium"
											label="Date occurred"
											onChange={val => {
												let date: string | null = null;
												if (val) {
													date = moment(val).format(ISO_DATE_FORMAT);
												}
												setFieldValue('effectiveDate', date);
											}}
											value={values.effectiveDate ? moment(values.effectiveDate) : null}
											fullWidth
											status={errors.effectiveDate && touched.effectiveDate ? 'error' : undefined}
											helperText={errors.effectiveDate}
										/>
										<DatePicker
											size="medium"
											label="Date recorded"
											onChange={val => {
												let date: string | null = null;
												if (val) {
													date = moment(val).format(ISO_DATE_FORMAT);
												}
												setFieldValue('dateRecorded', date);
											}}
											value={values.dateRecorded ? moment(values.dateRecorded) : null}
											fullWidth
											status={errors.dateRecorded && touched.effectiveDate ? 'error' : undefined}
											helperText={errors.dateRecorded}
										/>
										<Select<SwiftFoxGoalPointsCategory, false, true, false>
											fullWidth
											size="medium"
											options={pointCategories}
											getOptionLabel={o => o.name}
											value={pointCategories.find(p => p.id === values.pointCategory) ?? null}
											label="Point category"
											disableClearable
											onChange={(_, val) => {
												setFieldValue('pointCategory', val.id);
											}}
											status={errors.pointCategory && touched.effectiveDate ? 'error' : undefined}
											helperText={
												!pointCategories.length
													? 'Point categories have not been setup. Please contact us to get started.'
													: errors.pointCategory
											}
										/>
										<NumberInput
											value={values.points}
											label="Point value"
											size="medium"
											fixedDecimals={0}
											min={0}
											onChange={val => {
												const newVal = typeof val === 'number' ? val : 0;
												setFieldValue('points', newVal);
											}}
											fullWidth
											status={errors.points && touched.effectiveDate ? 'error' : undefined}
											helperText={errors.points}
										/>
										<Select<GoalsActivitySource>
											size="medium"
											options={activitySources ?? []}
											label={'Source'}
											value={values.source ?? null}
											isOptionEqualToValue={(o, v) => o.id === v.id}
											getOptionLabel={o => (typeof o === 'string' ? o : o.name) ?? ''}
											onChange={(_, val) => {
												setFieldValue('source', val);
												setFieldValue('sourceId', val.id);
											}}
											status={errors.source && touched.effectiveDate ? 'error' : undefined}
											helperText={errors.source as string}
										/>
										<FileDropperWithAttachments
											disableEditing={sasLoading}
											getSas={async fileName => {
												const sas = await getSasMutation({ contentId, fileName });
												return sas ?? '';
											}}
											currentAttachments={(values.documents ?? []) as Attachment[]}
											onAttachCompleted={async attachments => {
												setFieldValue('documents', attachments);
											}}
											allowedMimesMapping={COMMON_ATTACHMENT_FORMATS_MIME_MAP}
											deleteAttachment={async (index: number, url: string) => {
												const filteredAttachments = values.documents?.filter((_, i) => i !== index);
												setFieldValue('documents', filteredAttachments);
											}}
										/>
										<FlexRowSpaced>
											<Button disabled={isSubmitting} variant="tertiary" onClick={e => closeDrawer()}>
												Cancel
											</Button>
											<Button
												disabled={isSubmitting || !dirty}
												variant={'primary'}
												onClick={e => {
													submitForm();
												}}
											>
												{`${record?.id ? 'Update' : 'Add'} ${activityLabel.toLowerCase()}`}
											</Button>
										</FlexRowSpaced>
									</FlexColumnSpaced>
								</Padded>
							</Spin>
						</Form>
					);
				}}
			</Formik>
			{!strIsNullOrEmpty(error) ? <MuiAlert severity="error">{error}</MuiAlert> : null}
			{!strIsNullOrEmpty(sasError) ? <MuiAlert severity="error">{sasError}</MuiAlert> : null}
		</FlexColumnSpaced>
	);
}
